// Voice/provider to send with secondary TTS requests (flashcards, tapped words,
// lesson phrases, alternative responses).
//
// Japanese follows the selected voice (LAN-326): the composite voice id
// ("hina_female_inworld", "ash_male_openai", "ken_male_elevenlabs") carries the
// provider, so both the bare key and that provider are sent and the server
// resolves the Japanese policy (Yuki/Hina by default).
//
// Every other language keeps the historical behaviour byte-for-byte: ElevenLabs
// with the selected key when the selection is an ElevenLabs voice, otherwise
// ElevenLabs with no voice (server picks). Team-level `voice_provider` values
// are picker tiers ("v1"/"v2"/"v3") on the web, so they are never trusted as
// providers.
const KNOWN_PROVIDERS = ['elevenlabs', 'openai', 'cartesia', 'inworld'];

export function splitCompositeVoice(voice) {
  const value = String(voice || '').trim();
  if (!value) return { key: '', provider: null };

  const parts = value.split('_');
  const suffix = parts[parts.length - 1];
  const provider = parts.length >= 3 && KNOWN_PROVIDERS.includes(suffix) ? suffix : null;
  return { key: parts[0], provider };
}

// keepRotation: surfaces that historically sent no voice for other languages
// (flashcards, lesson progress) keep the server-side voice rotation there.
// passthroughProvider: surfaces that historically sent the user's real provider
// (call-mode alternative responses) keep doing so for other languages, falling
// back to ElevenLabs only when no provider can be resolved.
export function secondaryAudioVoiceOptions({ language, voice, voiceProvider, keepRotation = false, passthroughProvider = false } = {}) {
  const { key, provider: providerFromVoice } = splitCompositeVoice(voice);
  const explicitProvider = KNOWN_PROVIDERS.includes(String(voiceProvider || '').toLowerCase())
    ? String(voiceProvider).toLowerCase()
    : null;
  const provider = providerFromVoice || explicitProvider;

  if (passthroughProvider && String(language || '').toLowerCase() !== 'japanese') {
    return {
      voice_provider: provider || 'elevenlabs',
      ...(key && { voice: key })
    };
  }

  if (String(language || '').toLowerCase() === 'japanese') {
    return {
      voice_provider: provider || 'elevenlabs',
      ...(key && { voice: key })
    };
  }

  const useElevenLabsVoice = !keepRotation && key && (provider === 'elevenlabs' || (!provider && !voiceProvider));
  return {
    voice_provider: 'elevenlabs',
    ...(useElevenLabsVoice && { voice: key })
  };
}
